import React from 'react';
import { CareerLevel, PayoutLineBreakdown } from '../../types/career';
import { Badge } from '../ui/Badge';
import { formatAE, formatCurrencyUAH } from '../../utils/formatters';
import { getLevelTitle } from '../../math/careerRules';
import { TrendingUp, User } from 'lucide-react';

export interface DirectLineItemProps {
  item: PayoutLineBreakdown;
  mentorLevel: CareerLevel;
  onSelectLeader?: (leaderId: string) => void;
  isCompact?: boolean;
}

export const DirectLineItem: React.FC<DirectLineItemProps> = ({
  item,
  mentorLevel,
  onSelectLeader,
}) => {
  const leaderTitle = getLevelTitle(item.lineLeaderLevel);
  const isHigherOrEqual = item.lineLeaderLevel >= mentorLevel;

  return (
    <div
      onClick={() => onSelectLeader?.(item.lineLeaderId)}
      className={`
        glass-card rounded-xl p-3 sm:p-3.5 border border-white/5 hover:border-accent-emerald/30
        transition-all duration-200 space-y-2.5
        ${onSelectLeader ? 'cursor-pointer hover:bg-slate-800/60' : ''}
      `}
    >
      {/* Row 1: Leader Info & Calculated Line Commission */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-surface-hover border border-white/10 flex items-center justify-center text-accent-emerald flex-shrink-0">
            <User className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-text-primary text-xs sm:text-sm truncate">
                {item.lineLeaderName}
              </span>
              <Badge level={item.lineLeaderLevel} size="sm">
                Р{item.lineLeaderLevel} • {leaderTitle}
              </Badge>
            </div>
          </div>
        </div>

        {/* Total Commission on the right */}
        <div className="text-right flex-shrink-0">
          <div className="text-xs sm:text-sm font-bold font-mono text-accent-neon whitespace-nowrap flex items-center justify-end gap-1">
            <TrendingUp className="w-3.5 h-3.5 text-accent-neon hidden sm:inline" />
            {formatCurrencyUAH(item.lineCommissionUAH, 2)}
          </div>
        </div>
      </div>

      {/* Row 2: Metrics Strip (Turnover, Leader Rate, Difference) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t border-white/5 text-[11px] text-text-secondary bg-slate-900/40 p-2 rounded-lg">
        <div>
          <div className="text-[10px] text-text-muted">Обіг гілки:</div>
          <div className="font-mono font-bold text-text-primary text-xs">
            {formatAE(item.lineMonthlyTurnoverAE)}
          </div>
        </div>

        <div>
          <div className="text-[10px] text-text-muted">Ставка лінії:</div>
          <div className="font-mono text-slate-300">
            {formatCurrencyUAH(item.lineRate, 2)}
          </div>
        </div>

        <div className="col-span-2 sm:col-span-1">
          <div className="text-[10px] text-text-muted">Міжрівнева різниця:</div>
          <div className="font-mono font-bold text-cyan-400">
            {isHigherOrEqual ? (
              <span className="text-text-muted">0.00 ₴</span>
            ) : (
              <span>+{formatCurrencyUAH(item.netRateDifferenceUAH, 2)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
